import AltmetricBadge from '../../../../common/components/documentViewer/altmetricBadge';
import DocumentDetailsCollapse from '../../../../common/components/documentViewer/documentDetailsCollapse';
import DocumentDetailsRow, {
	IDocumentDetailsItem
} from '../../../../common/components/documentViewer/documentDetailsRow';
import GenreLozenge from '../../../../common/components/documentViewer/genreLozenge';
import { caption } from '../../../../common/styles/commonStyles';
import {
	ICitationDetails,
	ICitationDetailsViewModel,
	ICitationItem
} from '../../../../common/typings/citationDetails/citationDetailsViewModel';
import { commonTestIds } from '../../../../common/utils/commonTestIds';
import { toKebabCase } from '../../../../common/utils/stringUtils';
import { OrderViewerOptions } from '../../typing/enums';
import { IOrderViewerMainStore } from '../../typing/stores';
import { createStyles, makeStyles } from '@mui/styles';
import { inject, observer } from 'mobx-react';

const useStyles = makeStyles(() =>
	createStyles({
		root: {
			display: 'flex',
			flexDirection: 'column',
			gap: '0.25rem'
		},
		text: {
			...caption
		}
	})
);

export interface ICitationTabProps {
	detailsTabViewModel?: ICitationDetailsViewModel;
	options?: OrderViewerOptions;
}

const CitationDetailsTab: React.FunctionComponent<ICitationTabProps> = props => {
	const classes = useStyles();
	const tabViewModel = props.detailsTabViewModel;
	const details = tabViewModel.details;
	const relatedDetails = tabViewModel.relatedDetails;
	const useAltmetric = (props.options & OrderViewerOptions.UseAltmetric) === OrderViewerOptions.UseAltmetric;
	const pmid = details['PMID']?.value;
	const doi = details['DOI']?.value;
	let rowKey = 0;

	const createRow = (items: ICitationItem[] | ICitationItem, controlledBy = '') => {
		const citationItems = Array.isArray(items) ? items : [items];
		const citations: IDocumentDetailsItem[] = citationItems.map((citationItem: ICitationItem) => {
			const title = citationItem.labelText;
			const value = citationItem.value;
			return {
				title: title,
				value: value,
				testId: toKebabCase(title),
				controlledBy: controlledBy
			};
		});

		if (citations.length > 0) {
			return <DocumentDetailsRow key={rowKey++} items={citations} />;
		} else {
			return <></>;
		}
	};

	const createCitationRows = (citationDetails: ICitationDetails, controlledBy = '') => {
		const resultRows = [];
		const rows = [['Title'], ['Volume', 'Issue', 'Pages']];
		const added = [];

		resultRows.push(
			rows.map(items => {
				const citationItems = items
					.filter(title => citationDetails[title] != null)
					.map(title => {
						added.push(title);
						return citationDetails[title];
					});

				if (citationItems.length > 0) {
					return createRow(citationItems, controlledBy);
				}

				return <></>;
			})
		);

		resultRows.push(
			Object.keys(citationDetails).map(key => {
				if (!added.some(x => x === key)) {
					return createRow(citationDetails[key], controlledBy);
				}

				return <></>;
			})
		);

		return resultRows;
	};

	return (
		<div className={classes.root}>
			<GenreLozenge genre={tabViewModel.type} parentTitle="" />
			{createCitationRows(details, 'citation')}
			{useAltmetric && <AltmetricBadge pmid={pmid} doi={doi} />}
			{relatedDetails && (
				<DocumentDetailsCollapse title={localize('Related to')} collapsed={true} testId="related-to">
					{createCitationRows(relatedDetails, 'related-to')}
				</DocumentDetailsCollapse>
			)}
			{tabViewModel.abstract && (
				<DocumentDetailsCollapse title={localize('Abstract')} collapsed={true} testId="abstract">
					<p className={classes.text}>
						<span
							data-test-id={commonTestIds.abstractValue}
							data-controlled-by="abstract-section"
							dangerouslySetInnerHTML={{ __html: tabViewModel.abstract }}
						/>
					</p>
				</DocumentDetailsCollapse>
			)}
		</div>
	);
};

export default inject((stores: IOrderViewerMainStore) => ({
	detailsTabViewModel: stores.orderViewerStore.viewModel.citationDetailsViewModel,
	options: stores.orderViewerStore.viewModel.viewerOptions
}))(observer(CitationDetailsTab));
